import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MaintenanceLogsService } from '../services/logs.service';
import type { 
  MaintenanceLog, 
  LogFilters 
} from '../types/maintenance-logs.types';
import { useState } from 'react';

export function useLogs(companyId: string, initialFilters: LogFilters = {}) {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<LogFilters>({
    status: 'all',
    ...initialFilters
  });

  const query = useQuery({
    queryKey: ['maintenance-logs', companyId, filters],
    queryFn: () => MaintenanceLogsService.getLogs(companyId, filters),
    enabled: !!companyId,
  });

  const statsQuery = useQuery({
    queryKey: ['maintenance-logs-stats', companyId],
    queryFn: () => MaintenanceLogsService.getLogStats(companyId),
    enabled: !!companyId,
  });

  const createLogMutation = useMutation({
    mutationFn: (data: Partial<MaintenanceLog>) => MaintenanceLogsService.createLog(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-logs'] });
      queryClient.invalidateQueries({ queryKey: ['maintenance-logs-stats'] });
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string, status: 'approved' | 'rejected' | 'submitted' }) => 
      MaintenanceLogsService.updateStatus(id, status),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-logs'] });
      queryClient.invalidateQueries({ queryKey: ['maintenance-logs', 'detail', variables.id] });
    },
  });

  return {
    ...query,
    logs: query.data?.data || [],
    total: query.data?.total || 0,
    stats: statsQuery.data,
    isLoadingStats: statsQuery.isLoading,
    filters,
    setFilters,
    createLog: createLogMutation.mutateAsync,
    updateStatus: updateStatusMutation.mutateAsync,
    isCreating: createLogMutation.isPending,
    isUpdatingStatus: updateStatusMutation.isPending,
  };
}

export function useLogDetail(id?: string) {
  const mediaQuery = useQuery({
    queryKey: ['maintenance-logs', 'media', id],
    queryFn: () => MaintenanceLogsService.getLogMedia(id!),
    enabled: !!id,
  });

  const detailQuery = useQuery({
    queryKey: ['maintenance-logs', 'detail', id],
    queryFn: () => MaintenanceLogsService.getLogDetail(id!),
    enabled: !!id,
  });

  return {
    ...detailQuery,
    log: detailQuery.data,
    media: mediaQuery.data || [],
    isLoadingMedia: mediaQuery.isLoading,
  };
}
